import React, { memo, useMemo } from 'react';
import { DemeterCommodity, DemeterInsurancePositionModel, RunCalculationEngineResponse } from '../../../../../Generated/Raven-Demeter';
import Carousel from '../../../../Components/Carousel/Carousel';
import { IChartBarDataSeries, IChartDataSeries } from '../../../../Components/Charts/ChartDefinitions';
import ChartWrapper from '../../../../Components/Charts/ChartWrapper/ChartWrapper';
import StackedBarLineChartRaw from '../../../../Components/Charts/StackedBarLine/StackedBarLineChartRaw';
import LoadingSpinner from '../../../../Components/LoadingSpinner/LoadingSpinner';
import useLanguage from '../../../../Services/Language/useLanguageHook';
import { MarginCompositeModel, MarginQuarterValue } from '../../RiskDefinitions';
import hedgeMapChartsService, { ClassificationData } from './HedgeMapChartsService';
import styles from './MarginCharts.module.scss';

interface ILivestockRiskProtectionHedgeMapCarouselProps {
    filteredPositions: DemeterInsurancePositionModel[];
    runCalculationEngineResponse: RunCalculationEngineResponse;
    getFarmerMarginCalculatorCompositeRowsModels: MarginCompositeModel[];
    isLoading: boolean;
    displayDecimalPlaces: number;
    startingQuarter?: MarginQuarterValue;
    endingQuarter?: MarginQuarterValue;
}

const LivestockRiskProtectionHedgeMapCarousel: React.FC<ILivestockRiskProtectionHedgeMapCarouselProps> = (
    props: ILivestockRiskProtectionHedgeMapCarouselProps,
) => {
    const [translations] = useLanguage();

    const dataSourceTag = useMemo(() => translations.dataSource.StoneXCalculations, [translations]);

    const classificationGroups = useMemo<ClassificationData[]>(
        () => hedgeMapChartsService.groupPositionsBySubCommodity(props.filteredPositions),
        [props.filteredPositions],
    );

    const startOfCurrentMonth = useMemo(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1), []);

    const classificationCharts = useMemo(() => {
        const startingQuarterStartDate = props.startingQuarter ? new Date(props.startingQuarter.year, (props.startingQuarter.quarter - 1) * 3, 1) : null;
        const endingQuarterEndDate = props.endingQuarter ? new Date(props.endingQuarter.year, props.endingQuarter.quarter * 3 - 1, 1) : null;

        const filteredCompositeModels = props.getFarmerMarginCalculatorCompositeRowsModels.filter((compositeModel) => {
            const date = new Date(compositeModel.asOfDate);
            return date >= startOfCurrentMonth && (!endingQuarterEndDate || date <= endingQuarterEndDate);
        });

        return classificationGroups.map((classification) => {
            const currency = classification.positions?.[0]?.currency;
            const unitOfMeasure = classification.positions?.[0]?.unitOfMeasure;

            const allGroupedPositions = hedgeMapChartsService.buildLRPGroupedPositions(classification.positions);

            const groupedPositions = allGroupedPositions.filter(
                (group) =>
                    (!startingQuarterStartDate || group.periodDate >= startingQuarterStartDate) &&
                    (!endingQuarterEndDate || group.periodDate <= endingQuarterEndDate),
            );

            const barsSeries: IChartBarDataSeries[] =
                groupedPositions.length === 0
                    ? []
                    : [
                          {
                              label: translations.risk.margin.text.headInsured,
                              data: groupedPositions.map((group) => ({
                                  value: group.headInsured,
                                  asOfDate: group.periodDate,
                                  isActualValue: true,
                              })),
                              stack: 'headInsured',
                          },
                      ];

            const futuresBlendPriceSeries: IChartDataSeries = {
                label: translations.risk.margin.text.futuresPrice,
                data: hedgeMapChartsService.mapCompositeModelsToSeriesData(
                    props.runCalculationEngineResponse?.results,
                    filteredCompositeModels,
                    props.getFarmerMarginCalculatorCompositeRowsModels[0]?.futuresBlendPrice?.variableName ?? '',
                ),
            };

            const outputHedgedPriceSeries: IChartDataSeries = {
                label: translations.risk.margin.text.weightedAverageCoveragePrice,
                data: filteredCompositeModels.map((compositeModel) => ({
                    value:
                        hedgeMapChartsService.calculateLRPWeightedAverageInsurancePrice(props.runCalculationEngineResponse?.results, compositeModel.asOfDate) ??
                        0,
                    asOfDate: new Date(compositeModel.asOfDate),
                    isActualValue: true,
                })),
                dashStyle: 'Dot' as const,
            };

            const linesSeries: IChartDataSeries[] = [futuresBlendPriceSeries, outputHedgedPriceSeries];

            return {
                subCommodity: classification.subCommodity,
                barsSeries,
                linesSeries,
                currency,
                unitOfMeasure,
                groupedPositions,
            };
        });
    }, [
        classificationGroups,
        props.runCalculationEngineResponse,
        props.getFarmerMarginCalculatorCompositeRowsModels,
        props.startingQuarter,
        props.endingQuarter,
    ]);
    const isChartDataReady = useMemo(() => classificationCharts.some((chart) => chart.groupedPositions.length > 0), [classificationCharts]);

    if (classificationCharts.length === 0) {
        return null;
    }

    if (classificationCharts.length === 1) {
        const chart = classificationCharts[0];

        return (
            <ChartWrapper
                name="LivestockRiskProtectionHedgeMapCarousel"
                title={`${translations.risk.headers.marginHedgeMap} - ${translations.commodity[chart.subCommodity as DemeterCommodity]}`}
                dataSourceTag={dataSourceTag}
                isLoading={props.isLoading || !isChartDataReady}
                track={{ eventCategory: 'MarginCalculator', eventAction: 'LivestockRiskProtectionHedgeMapCarousel' }}
            >
                {isChartDataReady ? (
                    <StackedBarLineChartRaw
                        barsSeries={chart.barsSeries}
                        linesSeries={chart.linesSeries}
                        unitOfMeasure={chart.unitOfMeasure}
                        currency={chart.currency}
                        volumeAxisLabel={translations.risk.margin.headers.headCount}
                        displayDecimalPlacesMaximum={props.displayDecimalPlaces}
                        displayDecimalPlacesMinimum={props.displayDecimalPlaces}
                        useVerticalSeparation
                        showLineMarkers
                    />
                ) : (
                    <LoadingSpinner />
                )}
            </ChartWrapper>
        );
    }

    return (
        <div className={styles.margin_carousel_charts}>
            <Carousel track={{ eventCategory: 'MarginCalculator', eventAction: 'CarouselChartChanged' }}>
                {classificationCharts.map((chart) => (
                    <div key={chart.subCommodity} className={styles.margin_carousel_chart}>
                        <ChartWrapper
                            key={chart.subCommodity}
                            name="LivestockRiskProtectionHedgeMapCarousel"
                            title={`${translations.risk.headers.marginHedgeMap} - ${translations.commodity[chart.subCommodity as DemeterCommodity]}`}
                            dataSourceTag={dataSourceTag}
                            isLoading={props.isLoading || !isChartDataReady}
                            track={{ eventCategory: 'MarginCalculator', eventAction: 'LivestockRiskProtectionHedgeMapCarousel' }}
                        >
                            <StackedBarLineChartRaw
                                barsSeries={chart.barsSeries}
                                linesSeries={chart.linesSeries}
                                unitOfMeasure={chart.unitOfMeasure}
                                currency={chart.currency}
                                volumeAxisLabel={translations.risk.margin.headers.headCount}
                                displayDecimalPlacesMaximum={props.displayDecimalPlaces}
                                displayDecimalPlacesMinimum={props.displayDecimalPlaces}
                                useVerticalSeparation
                                showLineMarkers
                            />
                        </ChartWrapper>
                    </div>
                ))}
            </Carousel>
        </div>
    );
};

export default memo(LivestockRiskProtectionHedgeMapCarousel);
